/**
 * ``useAnnotationKeyboard`` — keyboard affordances for the annotation feature
 * (ADR-034 Inc 7; feature-spec §8 / D14; REQ-UI-059 keyboard operability).
 *
 *   - **Escape** clears the active draw tool + the current selection (D14 — the
 *     keyboard companion to re-clicking the tool / clicking empty map).
 *   - **Delete / Backspace** removes the selected annotation (the keyboard
 *     companion to the palette's Delete button).
 *
 * Both are no-ops while focus is in an editable control (``<input>`` /
 * ``<textarea>`` / ``contenteditable``) so typing a label is never hijacked,
 * and Escape/Delete with nothing active/selected fall through to any other
 * handler (no ``preventDefault``) so global shortcuts (close a menu, etc.)
 * still work.
 *
 * Mount once from a component inside the QueryClient subtree (the palette).
 *
 * Cross-references:
 *   - ADR-034; feature-spec §8 / D14; REQ-UI-059.
 *   - @/annotations/annotation-tool-store / use-annotations (delete).
 */
import { useEffect } from "react";
import { useAnnotationToolStore } from "@/annotations/annotation-tool-store";
import { useAnnotationDeleteConfirmStore } from "@/annotations/annotation-delete-confirm-store";

/** True when focus is in a text-editing control (so we must not steal the key). */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;
}

/** Wire the annotation keyboard shortcuts (Escape / Delete). Renders nothing. */
export function useAnnotationKeyboard(): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (isEditableTarget(event.target)) {
        return;
      }
      const store = useAnnotationToolStore.getState();
      if (event.key === "Escape") {
        if (store.activeTool !== null || store.selectedId !== null) {
          store.clearActiveTool();
          store.setSelectedId(null);
        }
        return;
      }
      if (event.key === "Delete" || event.key === "Backspace") {
        if (store.selectedId !== null) {
          event.preventDefault();
          // Open the confirmation dialog rather than deleting outright (#6).
          useAnnotationDeleteConfirmStore.getState().requestDelete(store.selectedId);
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);
}
